import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { DayPilotScheduler } from '@daypilot/daypilot-lite-react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import TodayIcon from '@mui/icons-material/Today';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import * as manufacturingApi from '../../lib/api/manufacturing';
import * as shipmentMgmtApi from '../../lib/api/shipmentMgmt';

const PHASE_STYLES = {
  design: { label: '설계', color: '#1976d2' },
  production: { label: '생산', color: '#ef6c00' },
  qc: { label: 'QC', color: '#7b1fa2' },
  shipment: { label: '출고', color: '#2e7d32' },
};

const createManufacturingMockPlans = (month) => {
  const base = dayjs(month).startOf('month');

  return [
    {
      planId: 'plan-mock-1',
      planNumber: 'MANU-202604-0001',
      projectId: 'project-mock-1',
      projectNumber: 'HC_PJ_2026_04_001',
      projectName: '시청 행정망 보안장비 구축',
      planStartDate: base.date(3).format('YYYY-MM-DD'),
      planEndDate: base.date(16).format('YYYY-MM-DD'),
      statusName: '진행중',
    },
    {
      planId: 'plan-mock-2',
      planNumber: 'MANU-202604-0002',
      projectId: 'project-mock-2',
      projectNumber: 'HC_PJ_2026_04_008',
      projectName: '공공기관 네트워크 교체',
      planStartDate: base.date(10).format('YYYY-MM-DD'),
      planEndDate: base.date(24).format('YYYY-MM-DD'),
      statusName: '설계전',
    },
  ];
};

const createManufacturingMockShipments = (month) => {
  const base = dayjs(month).startOf('month');

  return [
    {
      shipmentId: 'shipment-mock-1',
      shipmentNumber: 'SHP-202604-0001',
      productName: 'L2 스위치',
      customerNm: '평택시의회',
      shipmentDate: base.date(18).format('YYYY-MM-DD'),
      statusName: '출고대기',
    },
    {
      shipmentId: 'shipment-mock-2',
      shipmentNumber: 'SHP-202604-0002',
      productName: '보안 어플라이언스',
      customerNm: '유앤아이소프트',
      shipmentDate: base.date(27).format('YYYY-MM-DD'),
      statusName: '완료',
    },
  ];
};

const getListFromResponse = (response) => {
  const payload = response?.data?.data ?? response?.data;

  if (Array.isArray(payload?.content)) {
    return payload.content;
  }

  if (Array.isArray(payload?.list)) {
    return payload.list;
  }

  if (Array.isArray(payload)) {
    return payload;
  }

  return [];
};

const createPhaseEvents = (plan) => {
  const start = dayjs(plan.planStartDate);
  const end = dayjs(plan.planEndDate);

  if (!start.isValid() || !end.isValid() || end.isBefore(start)) {
    return [
      {
        id: `phase-${plan.planId}`,
        text: `${plan.planNumber} · ${plan.statusName || '계획'}`,
        start: dayjs().format('YYYY-MM-DD'),
        end: dayjs().add(1, 'day').format('YYYY-MM-DD'),
        resource: `plan-${plan.planId}`,
        backColor: '#78909c',
        borderColor: '#78909c',
        fontColor: '#ffffff',
        tags: {
          link: plan.planId
            ? `/manufacturingmng/${plan.projectId}/plans/${plan.planId}`
            : '/manufacturingmng',
        },
      },
    ];
  }

  const totalDays = Math.max(end.diff(start, 'day') + 1, 4);
  const designDays = Math.max(Math.round(totalDays * 0.2), 1);
  const productionDays = Math.max(Math.round(totalDays * 0.45), 1);
  const qcDays = Math.max(Math.round(totalDays * 0.2), 1);
  const usedDays = designDays + productionDays + qcDays;
  const shipmentDays = Math.max(totalDays - usedDays, 1);

  const phaseDefinitions = [
    { key: 'design', duration: designDays },
    { key: 'production', duration: productionDays },
    { key: 'qc', duration: qcDays },
    { key: 'shipment', duration: shipmentDays },
  ];

  let cursor = start;

  return phaseDefinitions.map((phase, index) => {
    const phaseStart = cursor;
    const phaseEndExclusive =
      index === phaseDefinitions.length - 1
        ? end.add(1, 'day')
        : cursor.add(phase.duration, 'day');

    cursor = phaseEndExclusive;

    return {
      id: `${plan.planId}-${phase.key}`,
      text: `${PHASE_STYLES[phase.key].label} · ${plan.planNumber}`,
      start: phaseStart.format('YYYY-MM-DD'),
      end: phaseEndExclusive.format('YYYY-MM-DD'),
      resource: `plan-${plan.planId}`,
      backColor: PHASE_STYLES[phase.key].color,
      borderColor: PHASE_STYLES[phase.key].color,
      fontColor: '#ffffff',
      toolTip: `${plan.projectName} · ${PHASE_STYLES[phase.key].label}`,
      tags: {
        link: plan.planId
          ? `/manufacturingmng/${plan.projectId}/plans/${plan.planId}`
          : '/manufacturingmng',
      },
    };
  });
};

const ManufacturingScheduleContainer = () => {
  const navigate = useNavigate();
  const [timelineStart, setTimelineStart] = useState(dayjs().startOf('month'));
  const [resources, setResources] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [usingMock, setUsingMock] = useState(false);

  const loadSchedule = useCallback(async () => {
    setLoading(true);
    setError(null);

    const rangeStart = timelineStart.startOf('month').format('YYYY-MM-DD');
    const rangeEnd = timelineStart.add(2, 'month').endOf('month').format(
      'YYYY-MM-DD',
    );

    try {
      const [plansRes, shipmentsRes] = await Promise.allSettled([
        manufacturingApi.getManufacturingPlans({
          page: 1,
          size: 100,
          startDate: rangeStart,
          endDate: rangeEnd,
        }),
        shipmentMgmtApi.getShipmentList({
          page: 1,
          size: 30,
          startDate: rangeStart,
          endDate: rangeEnd,
        }),
      ]);

      const planList =
        plansRes.status === 'fulfilled'
          ? getListFromResponse(plansRes.value)
          : [];
      const shipmentList =
        shipmentsRes.status === 'fulfilled'
          ? getListFromResponse(shipmentsRes.value)
          : [];

      const normalizedPlans = planList
        .filter((item) => item.planId || item.planNumber)
        .map((item, index) => ({
          planId: item.planId || `plan-row-${index}`,
          planNumber: item.planNumber || `PLAN-${index + 1}`,
          projectId: item.projectId || 'unknown-project',
          projectNumber: item.projectNumber || '-',
          projectName: item.projectName || item.planName || '생산계획',
          planStartDate: item.planStartDate || item.startDate || rangeStart,
          planEndDate:
            item.planEndDate || item.expectedCompletionDate || item.endDate || rangeEnd,
          statusName: item.statusName || item.status || '계획',
        }));

      const normalizedShipments = shipmentList
        .filter((item) => item.shipmentId || item.shipmentNumber)
        .map((item, index) => ({
          shipmentId: item.shipmentId || `shipment-row-${index}`,
          shipmentNumber: item.shipmentNumber || `SHP-${index + 1}`,
          productName: item.productName || item.stockProductName || '출고 품목',
          customerNm: item.customerNm || '-',
          shipmentDate: item.shipmentDate || rangeStart,
          statusName: item.statusName || item.status || '출고',
        }));

      const finalPlans =
        normalizedPlans.length > 0
          ? normalizedPlans
          : createManufacturingMockPlans(timelineStart);
      const finalShipments =
        normalizedShipments.length > 0
          ? normalizedShipments
          : createManufacturingMockShipments(timelineStart);

      setUsingMock(normalizedPlans.length === 0 && normalizedShipments.length === 0);

      const nextResources = [
        ...finalPlans.map((plan) => ({
          id: `plan-${plan.planId}`,
          name: `${plan.planNumber} · ${plan.projectName}`,
        })),
        ...finalShipments.map((shipment) => ({
          id: `shipment-${shipment.shipmentId}`,
          name: `[출고] ${shipment.shipmentNumber} · ${shipment.productName}`,
        })),
      ];

      const nextEvents = [
        ...finalPlans.flatMap((plan) => createPhaseEvents(plan)),
        ...finalShipments.map((shipment) => ({
          id: `shipment-event-${shipment.shipmentId}`,
          text: `출고 · ${shipment.customerNm}`,
          start: shipment.shipmentDate,
          end: dayjs(shipment.shipmentDate)
            .add(1, 'day')
            .format('YYYY-MM-DD'),
          resource: `shipment-${shipment.shipmentId}`,
          backColor: '#5d4037',
          borderColor: '#5d4037',
          fontColor: '#ffffff',
          toolTip: `${shipment.productName} · ${shipment.statusName}`,
          tags: {
            link: '/shipmentmng',
          },
        })),
      ];

      setResources(nextResources);
      setEvents(nextEvents);

      if (normalizedPlans.length === 0 && normalizedShipments.length === 0) {
        setError('조회 데이터가 없어 예시 제조 일정을 표시합니다.');
      }
    } catch (err) {
      const mockPlans = createManufacturingMockPlans(timelineStart);
      const mockShipments = createManufacturingMockShipments(timelineStart);

      setUsingMock(true);
      setResources([
        ...mockPlans.map((plan) => ({
          id: `plan-${plan.planId}`,
          name: `${plan.planNumber} · ${plan.projectName}`,
        })),
        ...mockShipments.map((shipment) => ({
          id: `shipment-${shipment.shipmentId}`,
          name: `[출고] ${shipment.shipmentNumber} · ${shipment.productName}`,
        })),
      ]);
      setEvents([
        ...mockPlans.flatMap((plan) => createPhaseEvents(plan)),
        ...mockShipments.map((shipment) => ({
          id: `shipment-event-${shipment.shipmentId}`,
          text: `출고 · ${shipment.customerNm}`,
          start: shipment.shipmentDate,
          end: dayjs(shipment.shipmentDate)
            .add(1, 'day')
            .format('YYYY-MM-DD'),
          resource: `shipment-${shipment.shipmentId}`,
          backColor: '#5d4037',
          borderColor: '#5d4037',
          fontColor: '#ffffff',
          tags: {
            link: '/shipmentmng',
          },
        })),
      ]);
      setError(
        err?.response?.data?.message ||
          '제조 일정을 불러오지 못해 예시 스케줄을 표시합니다.',
      );
    } finally {
      setLoading(false);
    }
  }, [timelineStart]);

  useEffect(() => {
    loadSchedule();
  }, [loadSchedule]);

  const summary = useMemo(() => {
    const shipmentCount = resources.filter((resource) =>
      resource.id.startsWith('shipment-'),
    ).length;
    const planCount = resources.filter((resource) =>
      resource.id.startsWith('plan-'),
    ).length;

    return {
      planCount,
      shipmentCount,
      eventCount: events.length,
    };
  }, [events, resources]);

  return (
    <Box>
      <Paper
        sx={{
          p: 2.5,
          mb: 2,
          borderRadius: 3,
          boxShadow: 'none',
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', lg: 'center' }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
              제조 스케줄러
            </Typography>
            <Typography variant="body2" color="text.secondary">
              생산계획과 출고 일정을 수평 타임라인으로 합쳐서 보는 Lite
              프로토타입입니다.
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip
              icon={<PrecisionManufacturingIcon />}
              label={`생산계획 ${summary.planCount}`}
              color="warning"
              variant="outlined"
            />
            <Chip
              icon={<LocalShippingIcon />}
              label={`출고 ${summary.shipmentCount}`}
              color="success"
              variant="outlined"
            />
            <Chip label={`이벤트 ${summary.eventCount}`} />
            {usingMock && <Chip label="예시 데이터" color="info" />}
          </Stack>
        </Stack>

        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1}
          sx={{ mt: 2 }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
        >
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIosNewIcon />}
              onClick={() =>
                setTimelineStart((prev) => prev.subtract(1, 'month'))
              }
            >
              이전 구간
            </Button>
            <Button
              variant="outlined"
              startIcon={<TodayIcon />}
              onClick={() => setTimelineStart(dayjs().startOf('month'))}
            >
              이번달 기준
            </Button>
            <Button
              variant="outlined"
              endIcon={<ArrowForwardIosIcon />}
              onClick={() => setTimelineStart((prev) => prev.add(1, 'month'))}
            >
              다음 구간
            </Button>
          </Stack>

          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {timelineStart.format('YYYY년 MM월')} ~{' '}
            {timelineStart.add(2, 'month').endOf('month').format('YYYY년 MM월')}
          </Typography>
        </Stack>
      </Paper>

      {error && (
        <Alert severity={usingMock ? 'info' : 'warning'} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper
        sx={{
          p: 2,
          borderRadius: 3,
          boxShadow: 'none',
          border: '1px solid',
          borderColor: 'divider',
          minHeight: 720,
        }}
      >
        {loading ? (
          <Box
            sx={{
              minHeight: 640,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <DayPilotScheduler
            startDate={timelineStart.format('YYYY-MM-DD')}
            days={90}
            scale="Day"
            cellWidth={34}
            rowHeaderWidth={300}
            locale="ko-kr"
            resources={resources}
            events={events}
            timeHeaders={[
              { groupBy: 'Month', format: 'yyyy년 M월' },
              { groupBy: 'Day', format: 'd' },
            ]}
            durationBarVisible={false}
            eventMoveHandling="Disabled"
            eventResizeHandling="Disabled"
            onEventClick={(args) => {
              const link = args?.e?.data?.tags?.link;
              if (link) {
                navigate(link);
              }
            }}
          />
        )}
      </Paper>
    </Box>
  );
};

export default ManufacturingScheduleContainer;
